import * as React from 'react'

import { makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'

import { IPermitTypeField } from '~/client/graph'
import StruxhubPhoneInput from '~/client/src/shared/components/StruxhubInputs/StruxhubPhoneInput'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import { getFormattedPhoneNumberForSubmission } from '~/client/src/shared/utils/phoneNumberHelpers'
import { isPhoneNumberValidForSubmission } from '~/client/src/shared/utils/phoneNumberValidation'

import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'
import PermitBaseFormField from './PermitBaseFormField'
import PermitFieldRemoveIcon from './PermitFieldRemoveIcon'

interface IProps {
  typeField: IPermitTypeField
  isViewMode: boolean

  store: SitePermitCreationFormStore
  fieldsStore: IPermitFieldsStore

  tableId?: string
  tableRowIndex?: number
  parentInstanceId?: string
  parentValueIndex?: number
}

@observer
export default class PermitFormPhoneNumberField extends React.Component<IProps> {
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)
    this.localProps = props
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  public render() {
    const {
      isViewMode,
      typeField,
      fieldsStore,
      parentInstanceId,
      parentValueIndex,
      store,
    } = this.props

    return (
      <PermitBaseFormField
        typeField={typeField}
        fieldValues={this.fieldValues}
        isViewMode={isViewMode}
        parentInstanceId={parentInstanceId}
        parentValueIndex={parentValueIndex}
        store={store}
        fieldsStore={fieldsStore}
        componentRenderer={this.renderComponent}
        onAddFieldClick={this.addNewFieldValue}
        onRemoveField={this.removeValue}
      />
    )
  }

  private renderComponent = (
    value: string,
    index: number,
    isHeaderShown?: boolean,
  ): JSX.Element => {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps
    const { caption, isMandatory, isMultiple } = typeField
    const { isFieldChanged, isFieldValid } = fieldsStore
    const shouldShowRemoveBtn = !!index || !!value

    return (
      <div className="row y-center">
        <StruxhubPhoneInput
          label={caption}
          isRequired={isMandatory}
          isChanged={isFieldChanged(
            typeField.id,
            index,
            tableId,
            tableRowIndex,
            parentInstanceId,
            parentValueIndex,
          )}
          isValid={isFieldValid(
            typeField,
            index,
            tableId,
            tableRowIndex,
            parentInstanceId,
            parentValueIndex,
          )}
          isLabelHidden={!!index && !isHeaderShown}
          isHelperTextCondensed={isMultiple && !isHeaderShown}
          isTopMarginForced={isMultiple && !index}
          value={value}
          onChange={this.onValueChange.bind(this, index)}
          validationMessage={Localization.translator.wrongPhoneNumberFormat}
        />
        {isMultiple && shouldShowRemoveBtn && !isHeaderShown && (
          <PermitFieldRemoveIcon index={index} onClick={this.removeValue} />
        )}
      </div>
    )
  }

  private get fieldValues(): string[] {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    const values = fieldsStore.getFieldValues<string>(
      typeField.id,
      tableId,
      tableRowIndex,
      false,
      parentInstanceId,
      parentValueIndex,
    )

    return values?.length ? values : ['']
  }

  private onValueChange(fieldIndex: number, phoneNumber: string) {
    const {
      fieldsStore,
      typeField,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.props
    const { changeFieldValue, changeTableFieldValue, changeValidationState } =
      fieldsStore

    const formattedValue = getFormattedPhoneNumberForSubmission(phoneNumber)

    if (formattedValue === this.fieldValues[fieldIndex]) {
      return
    }

    changeValidationState(
      typeField.id,
      fieldIndex,
      isPhoneNumberValidForSubmission(phoneNumber),
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    )

    if (this.isTableField) {
      changeTableFieldValue(
        tableId,
        tableRowIndex,
        typeField,
        fieldIndex,
        formattedValue,
        parentInstanceId,
        parentValueIndex,
      )
      return
    }

    changeFieldValue(
      typeField,
      fieldIndex,
      formattedValue,
      parentInstanceId,
      parentValueIndex,
    )
  }

  private addNewFieldValue = () => {
    if (!this.isTableField) {
      const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
        this.props

      fieldsStore.addNewFieldValue(
        typeField,
        '',
        parentInstanceId,
        parentValueIndex,
      )
    }
  }

  private removeValue = (index: number) => {
    if (!this.isTableField) {
      const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
        this.props
      const { rearrangeInvalidState, removeFieldValue } = fieldsStore

      rearrangeInvalidState(
        typeField.id,
        index,
        parentInstanceId,
        parentValueIndex,
      )
      removeFieldValue(typeField.id, index, parentInstanceId, parentValueIndex)
    }
  }

  private get isTableField(): boolean {
    return !!this.props.tableId
  }
}
